import React, { useCallback, useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Send, CheckCircle2, Lock, ShieldCheck, Loader2 } from "lucide-react";

function StatusPill({ h }) {
  if (!h.gated) return <span className="label-eyebrow text-sky-300" data-testid={`onb-status-${h.user_id}`}>EXEMPT (ADMIN)</span>;
  return h.complete
    ? <span className="label-eyebrow text-emerald-400 inline-flex items-center gap-1" data-testid={`onb-status-${h.user_id}`}><CheckCircle2 size={11} /> UNLOCKED</span>
    : <span className="label-eyebrow text-[var(--ff-red)] inline-flex items-center gap-1" data-testid={`onb-status-${h.user_id}`}><Lock size={11} /> LOCKED</span>;
}

export default function HostOnboardingStatus() {
  const [d, setD] = useState(null);
  const [sending, setSending] = useState(null);
  const load = useCallback(() => api.get("/admin/host-onboarding").then(({ data }) => setD(data)).catch((e) => toast.error(formatApiError(e.response?.data?.detail))), []);
  useEffect(() => { load(); }, [load]);

  const notify = async (h) => {
    setSending(h.user_id);
    try {
      const { data } = await api.post(`/admin/host-onboarding/${h.user_id}/notify`);
      data.ok ? toast.success(`Notice sent via ${data.method === "dm" ? "DM" : "channel ping"}`) : toast.error("Couldn't reach them — DMs closed and no fallback channel set in Site Settings");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSending(null); }
  };

  if (!d) return <div className="text-[var(--ff-text-dim)] font-mono-ui text-sm">Loading…</div>;
  return (
    <div className="space-y-4" data-testid="host-onboarding-status">
      <div className="flex flex-wrap gap-4 font-mono-ui text-[0.65rem] uppercase tracking-[0.15em]">
        <span className={d.members_intent === false ? "text-yellow-300" : "text-emerald-400"} data-testid="onb-members-intent">
          <ShieldCheck size={11} className="inline mr-1" />
          {d.members_intent === false ? "Server Members Intent OFF — Discord-side role grants not detected" : d.members_intent ? "Watching Discord for new Host roles" : "Bot connecting…"}
        </span>
        <span className={d.notice_channel_id ? "text-[var(--ff-text-dim)]" : "text-yellow-300"} data-testid="onb-fallback-channel">
          Fallback channel: {d.notice_channel_id ? "set" : "not set (Site Settings)"}
        </span>
      </div>
      <div className="surface-card divide-y divide-[var(--ff-border)]">
        {d.hosts.length === 0 && <div className="p-6 text-sm text-[var(--ff-text-dim)]">No hosts yet.</div>}
        {d.hosts.map((h) => (
          <div key={h.user_id} className="p-4 flex flex-wrap items-center gap-4" data-testid={`onb-host-${h.user_id}`}>
            {h.avatar ? <img src={h.avatar} alt="" className="w-8 h-8 rounded-full" /> : <div className="w-8 h-8 rounded-full bg-[var(--ff-red)] flex items-center justify-center font-display text-xs">{(h.name || "?")[0]}</div>}
            <div className="flex-1 min-w-[160px]">
              <div className="font-display">{h.name}</div>
              <div className="font-mono-ui text-[0.6rem] uppercase tracking-[0.12em] text-[var(--ff-text-dim)]">
                Tour {h.tour_completed_at ? "✓" : "—"} · Policies {h.read_count}/{h.total}
                {h.last_notice && ` · Notice ${h.last_notice.method} ${new Date(h.last_notice.at).toLocaleDateString()}`}
              </div>
            </div>
            <StatusPill h={h} />
            {h.gated && !h.complete && (
              <button onClick={() => notify(h)} disabled={sending === h.user_id} className="btn-ghost inline-flex items-center gap-1.5 text-[0.65rem] !px-3 !py-1.5" data-testid={`onb-notify-${h.user_id}`}>
                {sending === h.user_id ? <Loader2 size={11} className="animate-spin" /> : <Send size={11} />} Send Discord Notice
              </button>
            )}
          </div>
        ))}
      </div>
    </div>
  );
}
